import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DataAccessEventsFacade,
  DataAccessEventsPageFacade,
  DataAccessExhibitorsFacade,
  DataAccessExhibitorsPageFacade,
  DataAccessFairsFacade,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import { LinkClickFacade, ShareFacade } from '@planone/bau365/feature-tracking';
import { Contributor, Event, Exhibitor } from '@planone/bau365/util-fair-data';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import { BreadcrumbElement, ShareButtonsHelper } from '@planone/shared/ui-elements';
import { WindowRef } from '@planone/shared/util-browser-api';
import { LinkClickEvent } from '@planone/shared/util-interfaces';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { filterIsDefined } from '@planone/shared/util-rxjs-operator-type-helper';
import { BreadcrumbData, BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import { KeycloakService } from 'keycloak-angular';
import {
  combineLatest,
  combineLatestWith,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Component({
  selector: 'bau365-page-event-contributor-details',
  templateUrl: './page-event-contributor-details.component.html',
  styleUrls: ['./page-event-contributor-details.component.scss'],
})
export class PageEventContributorDetailsComponent implements OnInit, OnDestroy {
  exhibitor$: Observable<Exhibitor | undefined>;
  event$: Observable<Event | undefined>;
  contributor$: Observable<Contributor | undefined>;
  exhibitorLoading$: Observable<boolean | undefined>;
  exhibitorError$: Observable<string | undefined>;
  fairId$: Observable<string>;
  isBauInsights$: Observable<boolean>;

  pageTitle: string;
  contactPersonIndex = '';
  socialShareUrl: string | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;
  exhibitor: Exhibitor | undefined;
  event: Event | undefined;
  contactPerson: Contributor | undefined;

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private EventsFacade: DataAccessEventsFacade,
    private EventsPageFacade: DataAccessEventsPageFacade,
    private ExhibitorsFacade: DataAccessExhibitorsFacade,
    private ExhibitorsPageFacade: DataAccessExhibitorsPageFacade,
    private FairsFacade: DataAccessFairsFacade,
    private ScreensizeFacade: DataAccessScreensizeFacade,
    private trackingShareFacade: ShareFacade,
    private trackingLinkClickFacade: LinkClickFacade,
    private metaService: MetaService,
    private keycloakService: KeycloakService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private shareButtonsHelper: ShareButtonsHelper,
    private structuredDataGenerator: BreadcrumbGenerator,
    private renderer2: Renderer2,
    private breadcrumbHelper: BreadcrumbHelper,
    private windowRef: WindowRef,
    @Inject(BOT_REGEX) private botRegex: RegExp,
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.event$ = EventsFacade.event$;
    this.exhibitor$ = this.event$.pipe(
      filterIsDefined(),
      switchMap((event) => {
        return (this.exhibitor$ = ExhibitorsFacade.getExhibitorById(event.exhibitor_id));
      })
    );
    this.contributor$ = EventsFacade.contributor$;
    this.exhibitorLoading$ = ExhibitorsFacade.exhibitorLoading$;
    this.exhibitorError$ = ExhibitorsFacade.exhibitorError$;
    this.fairId$ = FairsFacade.fairId$;
    this.isBauInsights$ = FairsFacade.isBauInsights$;

    this.metaService.setTitle(localizedMeta['titleExhibitorContactPersonDetails'], [
      $localize`:@@pageMeta-titles-loading: Lädt...`,
    ]);

    this.pageTitle = this.metaService.getTitle(localizedMeta['titleExhibitorContactPersonDetails'], ['Anonymized']);
  }

  ngOnInit() {
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.EventsPageFacade.contributorPageView(params['id'], this.contactPersonIndex);
      this.contactPersonIndex = params['contributorId'];
      this.EventsFacade.contributorPageOpened(params['id'], this.contactPersonIndex, params['fair_id']);
    });

    this.FairsFacade.fairConfig$
      .pipe(combineLatestWith(this.isBauInsights$), takeUntil(this.unsubscribe))
      .subscribe(([fair, isBauInsights]) => {
        this.metaService.removeOpenGraphMetaTags();

        if (isBauInsights) {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleContactPersonPage'],
            localizedMeta['linkPreviewDescriptionContactPersonCompanyPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        } else {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleContactPersonPage'],
            localizedMeta['linkPreviewDescriptionContactPersonExhibitorPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        }
      });

    this.contributor$.pipe(takeUntil(this.unsubscribe)).subscribe((contributor) => {
      if (contributor) {
        this.contactPerson = { ...contributor };
        const first = contributor?.first_name ?? '';
        const second = contributor?.name ?? '';

        const name = (contributor?.title ? contributor?.title + ' ' : '') + first + ' ' + second;
        this.metaService.setTitle(localizedMeta['titleExhibitorContactPersonDetails'], [name ?? '']);
      }
    });

    combineLatest(this.exhibitor$, this.contributor$, this.event$)
      .pipe(withLatestFrom(this.FairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([[exhibitor, contributor, event], fair]) => {
        if (exhibitor) {
          this.exhibitor = exhibitor;
          this.event = event;

          if (contributor && event) {
            this.socialShareUrl = this.shareButtonsHelper.buildShareUrlFromPath(
              'fairs/' + exhibitor.fair_id + '/events/' + event.id + '/contributors/' + contributor.id
            );

            const breadcrumbData: BreadcrumbData = this.breadcrumbHelper.prepareForEventContributor(
              fair,
              exhibitor,
              event,
              contributor
            );

            this.breadcrumbElements = breadcrumbData.elements;

            this.structuredDataGenerator.validateBreadcrumbStructuredData(
              this.renderer2,
              breadcrumbData.structuredData,
              this.document.head
            );
          }
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataGenerator.removeTag(this.renderer2, this.document.head);
  }

  trackClick(clickEvent: LinkClickEvent) {
    if (this.exhibitor) {
      this.ExhibitorsPageFacade.trackedItemClicked(clickEvent);

      this.trackingLinkClickFacade.trigger({
        entityType: 'CONTRIBUTOR',
        linkType: clickEvent.linkType,
        linkText: clickEvent.linkText,
        context: this.exhibitor.fair_id ?? '',
        pageType: 'contributor',
        eventTarget: clickEvent.htmlElement ?? undefined,
      });
    }
  }

  trackShareDialogOpened() {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.exhibitor) {
        this.ExhibitorsPageFacade.shareDialogOpened('CONTACT-PERSON', 'contributor', fairId, this.exhibitor);

        this.trackingShareFacade.open({
          entityType: 'CONTRIBUTOR',
          pageType: 'contributor',
          context: fairId,
        });
      }
    });
  }

  trackShareButtonClicked(network: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.exhibitor && this.event && this.contactPerson) {
        this.ExhibitorsPageFacade.shareButtonClickedContactPerson(
          network,
          this.exhibitor,
          this.contactPerson,
          'CONTRIBUTOR',
          fairId,
          'contributor',
          this.event
        );

        this.trackingShareFacade.click({
          entityType: 'CONTRIBUTOR',
          pageType: 'contributor',
          context: fairId,
          network: network,
        });
      }
    });
  }
}
